import {useAuthInfo} from '@propelauth/react';
import _ from 'lodash';
import {ArrowLeft} from 'lucide-react';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Link as MuiLink,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';

import MinusIcon from '../assets/minus.svg';
import PlusIcon from '../assets/plus.svg';
import ExternalFolderIcon from '../assets/folder-external.svg';
import FolderIcon from '../assets/folder-internal.svg';
import {CreateDocumentModal} from '../components/documents/CreateDocument';
import Loading from '../components/Loading';
import CreateFolderModal from '../components/report-workspace/DocumentFolder/createNewFolderModal';
import DeleteFolderModal from '../components/report-workspace/DocumentFolder/deleteFolderModal';
import MoveFolderModal from '../components/report-workspace/DocumentFolder/moveFolderModal';
import RenameFolderModal from '../components/report-workspace/DocumentFolder/renameFolderModal';
import SearchBar from '../components/report-workspace/shared/searchBar';
import {
  CREATE_FLOWS,
  DOCUMENT_TYPES,
  FOLDER_PURPOSES,
  INGESTION_STATUSES,
} from '../constants';
import {setReadOnly} from '../data/documents';
import {
  useGetDocumentsQuery,
  useGetFoldersQuery,
} from '../data/documentWorkspaceApi';
import {setFolders, setFolderYears, setSelectedFolder} from '../data/folders';

const styles = {
  header: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 3rem',
  },
  menu: {
    base: {
      marginTop: '0.75rem',
      borderRadius: '0.25rem',
      border: '1px #E2E5E7',
      boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
      '.MuiMenu-list': {
        padding: '0.5rem',
      },
      '.MuiMenuItem-root': {
        width: '100%',
      },
      '.MuiList-root': {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      },
    },
    item: {
      borderRadius: '0.25rem',
      width: '12.25rem',
      padding: '0.5rem',
      '.MuiListItemIcon-root': {
        minWidth: '2rem',
      },
    },
    listText: {
      primary: {
        color: '#043B34',
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '1.25rem',
        marginLeft: '0.25rem',
      },
    },
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    paddingTop: '5rem',
    paddingBottom: '1.25rem',
    width: '100%',
    maxWidth: '1268px',
  },
  scrollableContent: {
    flex: 1,
    width: '100%',
    maxWidth: '1268px',
    overflowY: 'auto',
    paddingBottom: '5rem',
  },
  listItem: {
    maxWidth: '1268px',
    height: '100%',
    border: '1px solid #DFE5E2',
    marginBottom: '1rem',
    borderRadius: '12px',
    padding: '24px',
    paddingLeft: '32px',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
  },
  folderMenu: {
    mt: 1,
    minWidth: '200px',
    borderRadius: '4px',
    boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
    '& .MuiList-root': {
      padding: '8px',
    },
  },
  folderMenuItem: {
    fontSize: '14px',
    padding: '8px 16px',
    borderRadius: '4px',
    margin: '2px 0',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  pulseAnimation: {
    animation: 'pulse-fade 4s ease-out forwards',
    '@keyframes pulse-fade': {
      '0%': {backgroundColor: '#F2F4F4'},
      '10%': {backgroundColor: '#EBFAF1'},
      '85%': {backgroundColor: '#EBFAF1'},
      '100%': {backgroundColor: '#F2F4F4'},
    },
  },
};

const FolderCard = React.memo(({folder, onMenuClick, newlyCreatedFolderId}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {readOnly} = useSelector((state) => state.documents);
  const onFolderClick = useCallback(
    (folder) => {
      navigate(`/folders/${folder.id}/documents`);
      dispatch(setSelectedFolder({selectedFolder: folder}));
    },
    [navigate, dispatch]
  );

  const handleMenuClick = useCallback(
    (e) => {
      onMenuClick(e, folder);
    },
    [onMenuClick, folder]
  );

  const isExternalFolder = folder.purpose === FOLDER_PURPOSES.pdfReview;

  return (
    <Box
      sx={{
        width: '100%',
        height: '5rem',
        borderRadius: '12px',
        backgroundColor: isExternalFolder ? '#FFFFFF' : '#F2F4F4',
        border: isExternalFolder ? '1px solid #0F4B47' : 'none',
        padding: '1rem',
        position: 'relative',
        boxSizing: 'border-box',
        cursor: 'pointer',
        '&:hover': {
          border: '1px solid #054A41',
          backgroundColor: isExternalFolder ? '#F0F7F6' : '#F2F4F4',
        },
        ...(newlyCreatedFolderId === folder.id && styles.pulseAnimation),
      }}
      onClick={() => onFolderClick(folder)}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flex={1}
        sx={{minWidth: 0}}
      >
        {isExternalFolder ? (
          <img
            src={ExternalFolderIcon}
            alt="External Folder"
            width={24}
            height={24}
          />
        ) : (
          <img src={FolderIcon} alt="Folder" width={24} height={24} />
        )}
        <Stack direction="column" alignItems="left" sx={{minWidth: 0, flex: 1}}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              letterSpacing: '-0.16px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '90%',
              color: '#162523',
              lineHeight: '150%',
            }}
            title={folder.name}
          >
            {folder.name}
          </Typography>
          <Typography variant="caption" sx={{color: '#819190'}}>
            {folder.documentCount} documents
            {isExternalFolder ? ' • external' : ''}
          </Typography>
        </Stack>
      </Stack>
      {!readOnly && (
        <IconButton
          sx={{
            position: 'absolute',
            top: 20,
            right: 8,
            zIndex: 10,
            borderRadius: '50%',
          }}
          onClick={handleMenuClick}
        >
          <MoreVertIcon />
        </IconButton>
      )}
    </Box>
  );
});

FolderCard.displayName = 'FolderCard';
FolderCard.propTypes = {
  folder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    documentCount: PropTypes.number.isRequired,
    purpose: PropTypes.string.isRequired,
  }).isRequired,
  onMenuClick: PropTypes.func.isRequired,
  newlyCreatedFolderId: PropTypes.string,
};

const FoldersPage = () => {
  const {accessHelper} = useAuthInfo();
  const dispatch = useDispatch();
  const {isAuthenticated, currentOrg, hasParentOrg} = useSelector(
    (state) => state.session
  );
  const folders = useSelector((state) => state.folders.folders);
  const {readOnly} = useSelector((state) => state.documents);
  const [openMoveFolderModal, setOpenMoveFolderModal] = useState(false);
  const [openRenameFolderModal, setOpenRenameFolderModal] = useState(false);
  const [openDeleteFolderModal, setOpenDeleteFolderModal] = useState(false);
  const [openCreateFolderModal, setOpenCreateFolderModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedYears, setExpandedYears] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [newlyCreatedFolderId, setNewlyCreatedFolderId] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openCreateFileModal, setOpenCreateFileModal] = useState(false);
  const [openCreatePdfModal, setOpenCreatePdfModal] = useState(false);
  const [openUploadPdfModal, setOpenUploadPdfModal] = useState(false);
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);

  // Document creation state
  const [previousDocumentId, setPreviousDocumentId] = useState(null);
  const [existingDocumentId, setExistingDocumentId] = useState(null);
  const [sourceDocumentType, setSourceDocumentType] = useState(null);
  const [createFlow, setCreateFlow] = useState(null);

  const {
    data: foldersData,
    isLoading,
    error,
  } = useGetFoldersQuery(
    {},
    {
      skip: !isAuthenticated || !currentOrg?.orgId,
    }
  );
  const {data: documentsData, refetch: refetchDocuments} = useGetDocumentsQuery(
    {},
    {skip: !isAuthenticated || !currentOrg?.orgId}
  );

  // Filter documents for rollforward (annual and quarterly reports only)
  const filteredPreviousDocuments = useMemo(() => {
    if (!documentsData) return [];
    return _.orderBy(
      _.filter(documentsData, (doc) => {
        return (
          (doc.type === DOCUMENT_TYPES.annualReport ||
            doc.type === DOCUMENT_TYPES.quarterlyReport ||
            doc.type === DOCUMENT_TYPES.semiAnnualReport) &&
          doc?.ingestionState?.status !== INGESTION_STATUSES.failed &&
          doc?.ingestionState?.status === INGESTION_STATUSES.completed
        );
      }),
      ['fiscalYear', 'name'],
      ['desc', 'asc']
    );
  }, [documentsData]);

  const searchData = useMemo(() => {
    // Filter out documents that are rollforwards but not completed
    const filterDocuments = (documents) => {
      return documents.filter((item) => {
        // If it's a document (not a folder), check ingestion criteria
        if (item.type && item.type !== 'folder') {
          const itemIsRollForward =
            item?.ingestionState?.type === 'rollforward';
          const itemIsUpload = item?.ingestionState?.type === 'upload';
          const isCompleted =
            item?.ingestionState?.status === INGESTION_STATUSES.completed;

          // Exclude if it's a rollforward or upload and not completed
          if ((itemIsRollForward || itemIsUpload) && !isCompleted) {
            return false;
          }
        }
        return true;
      });
    };

    if (foldersData && documentsData) {
      const filteredDocuments = filterDocuments(documentsData);
      return _.orderBy(
        [...foldersData, ...filteredDocuments],
        [(item) => item.updatedAt || ''],
        ['desc']
      );
    }
    return [];
  }, [documentsData, foldersData]);

  const handleToggleYear = useCallback((year) => {
    setExpandedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  }, []);

  const handleMenuClick = useCallback((event, folder) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedFolder(folder);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleCloseMoveFolderModal = useCallback(() => {
    setOpenMoveFolderModal(false);
  }, []);

  const handleMoveYear = useCallback(() => {
    handleMenuClose();
    if (selectedFolder) {
      setOpenMoveFolderModal(true);
    }
  }, [selectedFolder, handleMenuClose]);

  const handleCloseRenameFolderModal = useCallback(() => {
    setOpenRenameFolderModal(false);
  }, []);

  const handleCloseDeleteFolderModal = useCallback(() => {
    setOpenDeleteFolderModal(false);
  }, []);

  const handleCloseCreateFolderModal = useCallback(() => {
    setOpenCreateFolderModal(false);
  }, []);

  const handleShowToast = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const handleHideToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const handleRename = useCallback(() => {
    if (selectedFolder) {
      setOpenRenameFolderModal(true);
    }
    handleMenuClose();
  }, [selectedFolder, handleMenuClose]);

  const handleDelete = useCallback(() => {
    if (selectedFolder) {
      setOpenDeleteFolderModal(true);
    }
    handleMenuClose();
  }, [selectedFolder, handleMenuClose]);

  // Error handling functions
  const _onShowError = useCallback(
    (
      errorMessage = 'There was an error processing your request. Please try again later.'
    ) => {
      handleShowToast(errorMessage);
    },
    [handleShowToast]
  );

  // Document creation callbacks
  const _onRollForwardComplete = useCallback(() => {
    refetchDocuments();
    handleShowToast('Rollforward completed successfully!');
  }, [refetchDocuments, handleShowToast]);

  const _onUploadComplete = useCallback(() => {
    refetchDocuments();
    handleShowToast('Upload completed successfully!');
  }, [refetchDocuments, handleShowToast]);

  const _onDocumentCreated = useCallback(() => {
    refetchDocuments();
    handleShowToast('Document created successfully!');
  }, [refetchDocuments, handleShowToast]);

  const processedFoldersData = useMemo(() => {
    if (!foldersData) return {sortedFolders: [], years: []};

    const sortMap = foldersData.reduce((acc, folder) => {
      const year = folder.year;
      if (!acc[year]) {
        acc[year] = [folder];
      } else {
        acc[year].push(folder);
      }
      return acc;
    }, {});

    Object.keys(sortMap).forEach((year) =>
      sortMap[year].sort((a, b) => {
        // Check if either folder is named 'Other' or 'other'
        const aIsOther = a.name.toLowerCase() === 'other';
        const bIsOther = b.name.toLowerCase() === 'other';

        // If both are 'other', maintain original order
        if (aIsOther && bIsOther) return 0;

        // If only 'a' is 'other', it should come last
        if (aIsOther) return 1;

        // If only 'b' is 'other', it should come last
        if (bIsOther) return -1;

        // Otherwise, sort alphabetically
        return a.name.localeCompare(b.name);
      })
    );

    const sortedFolders = Object.entries(sortMap).sort(
      (a, b) => Number(b[0]) - Number(a[0])
    );
    const years = Object.keys(sortMap).sort((a, b) => Number(b) - Number(a));

    return {sortedFolders, years};
  }, [foldersData]);

  useEffect(() => {
    if (currentOrg && accessHelper) {
      const canEdit = accessHelper.hasPermission(
        currentOrg.orgId,
        'can_edit_reports'
      );
      dispatch(setReadOnly({readOnly: !canEdit}));
    }
  }, [currentOrg, accessHelper, dispatch]);

  useEffect(() => {
    const {sortedFolders, years} = processedFoldersData;

    if (sortedFolders.length > 0) {
      if (expandedYears.length === 0 && years.length > 0) {
        setExpandedYears([years[0]]);
      }
      dispatch(setFolders({folders: sortedFolders}));
      dispatch(setFolderYears({folderYears: years}));
    }
  }, [processedFoldersData, dispatch]);

  if (!isAuthenticated) {
    return (
      <div style={styles.header}>
        <Alert severity="warning">Please log in to view folders</Alert>
      </div>
    );
  }

  if (!currentOrg?.orgId) {
    return (
      <div style={styles.header}>
        <Alert severity="info">Please select an organization</Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={styles.header}>
        <Loading loading={isLoading} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.header}>
        <Alert severity="error">
          Error loading folders:{' '}
          {error.data || error.message || 'Unknown error'}
        </Alert>
      </div>
    );
  }

  return (
    <div style={styles.header}>
      <div style={styles.stickyHeader}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          pt={3.5}
          sx={{width: '100%'}}
        >
          {hasParentOrg && (
            <Stack direction="column" alignItems="left" spacing={0.5}>
              <Breadcrumbs
                separator={
                  <ChevronRightIcon
                    sx={{color: '#162523', width: 20, height: 20}}
                  />
                }
                aria-label="breadcrumb"
                sx={{mb: 0}}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    marginBottom: '0.5rem',
                  }}
                >
                  <IconButton
                    component={Link}
                    to="/parent-organization"
                    sx={{
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ArrowLeft size={20} color="#162523" />
                  </IconButton>
                  <MuiLink
                    component={Link}
                    to="/parent-organization"
                    color="#162523"
                    sx={{
                      textDecoration: 'none',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '150%',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    All companies
                  </MuiLink>
                </Box>
              </Breadcrumbs>
              <Typography variant="h1">{currentOrg?.orgName}</Typography>
            </Stack>
          )}
          {!hasParentOrg && <Typography variant="h1">Folders</Typography>}
          {!readOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                background: '#26A993',
                borderRadius: '0.5rem',
                paddingLeft: '0.75rem',
                paddingRight: '1rem',
                paddingVertical: '0.625rem',
                height: '2.5rem',
                '&:hover': {
                  backgroundColor: '#26A993',
                },
              }}
              onClick={(event) => {
                setCreateMenuAnchorEl(event.currentTarget);
                setOpenCreateModal(true);
              }}
            >
              Create new
            </Button>
          )}
          <Menu
            anchorEl={createMenuAnchorEl}
            open={openCreateModal}
            onClose={() => {
              setOpenCreateModal(false);
              setCreateMenuAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={styles.menu.base}
            slotProps={{
              paper: {
                sx: {
                  boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
                  borderRadius: '4px',
                },
              },
            }}
          >
            <MenuItem
              sx={styles.menu.item}
              onClick={() => {
                setOpenCreateModal(false);
                setCreateMenuAnchorEl(null);
                setOpenCreateFolderModal(true);
              }}
            >
              <Typography sx={styles.menu.listText.primary}>
                New folder
              </Typography>
            </MenuItem>
            <MenuItem
              sx={styles.menu.item}
              onClick={() => {
                setOpenCreateModal(false);
                setCreateMenuAnchorEl(null);
                setCreateFlow(CREATE_FLOWS.upload);
                setOpenUploadPdfModal(true);
              }}
            >
              <Typography sx={styles.menu.listText.primary}>
                Upload PDF
              </Typography>
            </MenuItem>
            <MenuItem
              sx={styles.menu.item}
              onClick={() => {
                setOpenCreateModal(false);
                setCreateMenuAnchorEl(null);
                setCreateFlow(CREATE_FLOWS.pdf);
                setOpenCreatePdfModal(true);
              }}
            >
              <Typography sx={styles.menu.listText.primary}>
                Upload & rollforward PDF
              </Typography>
            </MenuItem>
          </Menu>
        </Stack>
        <SearchBar searchData={searchData} />
      </div>
      {openMoveFolderModal && (
        <MoveFolderModal
          open={openMoveFolderModal}
          onClose={handleCloseMoveFolderModal}
          folder={selectedFolder}
          onSuccess={(message) => handleShowToast(message)}
          onError={(message) => handleShowToast(message)}
          expandedYears={expandedYears}
          setExpandedYears={setExpandedYears}
        />
      )}
      {openRenameFolderModal && (
        <RenameFolderModal
          open={openRenameFolderModal}
          onClose={handleCloseRenameFolderModal}
          folder={selectedFolder}
          onSuccess={(message) => handleShowToast(message)}
          onError={(message) => handleShowToast(message)}
        />
      )}
      {openDeleteFolderModal && (
        <DeleteFolderModal
          open={openDeleteFolderModal}
          onClose={handleCloseDeleteFolderModal}
          folder={selectedFolder}
          onSuccess={(message) => handleShowToast(message)}
          onError={(message) => handleShowToast(message)}
        />
      )}
      {openCreateFolderModal && (
        <CreateFolderModal
          open={openCreateFolderModal}
          onClose={handleCloseCreateFolderModal}
          onSuccess={(message) => handleShowToast(message)}
          onError={(message) => handleShowToast(message)}
          expandedYears={expandedYears}
          setExpandedYears={setExpandedYears}
          setNewlyCreatedFolderId={setNewlyCreatedFolderId}
        />
      )}
      {openCreateFileModal && (
        <CreateDocumentModal
          open={openCreateFileModal}
          onClose={() => {
            setOpenCreateFileModal(false);
            setCreateFlow(null);
            setPreviousDocumentId(null);
            setExistingDocumentId(null);
            setSourceDocumentType(null);
          }}
          onError={_onShowError}
          previousDocuments={filteredPreviousDocuments || []}
          previousDocumentId={previousDocumentId}
          existingDocumentId={existingDocumentId}
          createFlow={createFlow || CREATE_FLOWS.blankDocument}
          sourceDocumentType={sourceDocumentType}
          onRollForwardComplete={_onRollForwardComplete}
          onDocumentCreated={_onDocumentCreated}
        />
      )}
      {openCreatePdfModal && (
        <CreateDocumentModal
          open={openCreatePdfModal}
          onClose={() => {
            setOpenCreatePdfModal(false);
            setCreateFlow(null);
            setPreviousDocumentId(null);
            setExistingDocumentId(null);
            setSourceDocumentType(null);
          }}
          onError={_onShowError}
          previousDocuments={filteredPreviousDocuments || []}
          previousDocumentId={previousDocumentId}
          existingDocumentId={existingDocumentId}
          createFlow={createFlow || CREATE_FLOWS.pdf}
          sourceDocumentType={sourceDocumentType}
          onRollForwardComplete={_onRollForwardComplete}
          onDocumentCreated={_onDocumentCreated}
        />
      )}
      {openUploadPdfModal && (
        <CreateDocumentModal
          open={openUploadPdfModal}
          onClose={() => {
            setOpenUploadPdfModal(false);
            setCreateFlow(null);
            setPreviousDocumentId(null);
            setExistingDocumentId(null);
            setSourceDocumentType(null);
          }}
          onError={_onShowError}
          createFlow={CREATE_FLOWS.upload}
          sourceDocumentType={sourceDocumentType}
          onUploadComplete={_onUploadComplete}
          onDocumentCreated={_onDocumentCreated}
        />
      )}
      <div style={styles.scrollableContent}>
        <List>
          {folders && folders.length > 0 ? (
            folders.map(([year, yearFolders]) => (
              <ListItem
                key={year}
                style={styles.listItem}
                onClick={() => handleToggleYear(year)}
              >
                <Stack
                  direction="column"
                  spacing={2}
                  sx={{width: '100%', marginTop: '5px'}}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{width: '100%', pt: 1}}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '26px',
                        letterSpacing: '-0.2px',
                      }}
                    >
                      {year}
                    </Typography>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleYear(year);
                      }}
                    >
                      {expandedYears.includes(year) ? (
                        <img src={MinusIcon} alt="Minus" />
                      ) : (
                        <img src={PlusIcon} alt="Plus" />
                      )}
                    </IconButton>
                  </Stack>
                  <Grid
                    container
                    sx={{
                      width: '100%',
                      margin: 0,
                    }}
                  >
                    {expandedYears.includes(year) &&
                      yearFolders.map((folder) => (
                        <Grid
                          item
                          xs={4}
                          key={folder.id}
                          sx={{
                            paddingLeft: '0 !important',
                            paddingTop: '0 !important',
                            marginBottom: '1.5rem',
                            paddingRight: '1.5rem',
                          }}
                        >
                          <FolderCard
                            folder={folder}
                            onMenuClick={handleMenuClick}
                            newlyCreatedFolderId={newlyCreatedFolderId}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Stack>
              </ListItem>
            ))
          ) : (
            <ListItem style={styles.listItem}>
              <Typography variant="body1" color="text.secondary">
                No folders found. Create your first folder to get started.
              </Typography>
            </ListItem>
          )}
        </List>
      </div>

      <FolderMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onMoveYear={handleMoveYear}
        onRename={handleRename}
        onDelete={() => handleDelete('Deleting')}
      />
      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={handleHideToast}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      >
        <Alert
          onClose={handleHideToast}
          severity={toastMessage.includes('Failed') ? 'error' : 'success'}
          icon={
            toastMessage.includes('Failed') ? (
              false
            ) : (
              <Box
                sx={{
                  backgroundColor: '#26A993',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CheckIcon sx={{color: 'white', fontSize: 16}} />
              </Box>
            )
          }
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: 'none',
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    session: state.session,
    folders: state.folders.folders, // Return the array, not the entire folders state object
  };
};

FoldersPage.propTypes = {
  session: PropTypes.object.isRequired,
  folders: PropTypes.array.isRequired,
};

export default connect(mapStateToProps)(FoldersPage);

const FolderMenu = ({
  anchorEl,
  open,
  onClose,
  onMoveYear,
  onRename,
  onDelete,
}) => {
  const handleMenuItemClick = (action) => {
    switch (action) {
      case 'move':
        onMoveYear && onMoveYear();
        break;
      case 'rename':
        onRename && onRename();
        break;
      case 'delete':
        onDelete && onDelete();
        break;
      default:
        break;
    }
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: styles.folderMenu,
        },
      }}
    >
      {/*
      <MenuItem
        sx={styles.folderMenuItem}
        onClick={() => handleMenuItemClick('move')}
      >
        Move year
      </MenuItem>
      */}
      <MenuItem
        sx={styles.folderMenuItem}
        onClick={() => handleMenuItemClick('rename')}
      >
        Rename
      </MenuItem>
      <MenuItem
        sx={{...styles.folderMenuItem, color: '#FF5D5D'}}
        onClick={() => handleMenuItemClick('delete')}
      >
        Delete
      </MenuItem>
    </Menu>
  );
};

FolderMenu.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMoveYear: PropTypes.func,
  onRename: PropTypes.func,
  onDelete: PropTypes.func,
};
